import React from 'react';
import { useIntl } from 'react-intl';
import { PiArrowCounterClockwise, PiArrowsDownUp, PiX } from 'react-icons/pi';
import { axisColors, clampTilt, cssColor, tiltAxes } from '../viewport';
import type { SectionAxis, SectionSettings } from '../viewport';
import ChromeButton from './ChromeButton';
import { messages } from './messages';
import SectionValueField from './SectionValueField';
import { AxisChip, Bar, Divider } from './SectionBar.styled';

const AXES: readonly SectionAxis[] = ['x', 'y', 'z'];

export interface SectionBarProps {
  section: SectionSettings;
  isDark: boolean;
  /** Where the cut lies along its axis, in millimetres. */
  offset: number;
  onChange: (section: SectionSettings) => void;
  onOffsetChange: (offset: number) => void;
  onExit: () => void;
  /** Docked above the bottom bar on a narrow viewer, otherwise pinned beside the cut. */
  docked: boolean;
}

const SectionBar = React.forwardRef<HTMLDivElement, SectionBarProps>(function SectionBar(
  { section, isDark, offset, onChange, onOffsetChange, onExit, docked },
  ref,
) {
  const intl = useIntl();
  const colors = axisColors(isDark);
  const tilted = section.tilt[0] !== 0 || section.tilt[1] !== 0;
  const [first, second] = tiltAxes(section.axis);
  const rotate = (axis: SectionAxis) =>
    intl.formatMessage(messages.sectionRotate, { axis: axis.toUpperCase() });
  const setTilt = (index: 0 | 1, degrees: number) => {
    const tilt: [number, number] = [...section.tilt];
    tilt[index] = clampTilt(degrees);
    onChange({ ...section, tilt });
  };
  return (
    <Bar
      ref={ref}
      $docked={docked}
      role="group"
      aria-label={intl.formatMessage(messages.section)}
      data-testid="model-viewer__section-bar"
    >
      {AXES.map((axis) => (
        <AxisChip
          key={axis}
          type="button"
          $color={cssColor(colors[axis])}
          $active={section.axis === axis}
          aria-pressed={section.axis === axis}
          onClick={() => onChange({ ...section, axis, tilt: [0, 0] })}
          data-testid={`model-viewer__section-axis-${axis}`}
        >
          {axis.toUpperCase()}
        </AxisChip>
      ))}
      <Divider />
      <ChromeButton
        label={intl.formatMessage(messages.sectionFlip)}
        icon={<PiArrowsDownUp />}
        pressed={section.flipped}
        onClick={() => onChange({ ...section, flipped: !section.flipped })}
        testId="model-viewer__section-flip"
      />
      {tilted && (
        <ChromeButton
          label={intl.formatMessage(messages.sectionResetRotation)}
          icon={<PiArrowCounterClockwise />}
          onClick={() => onChange({ ...section, tilt: [0, 0] })}
          testId="model-viewer__section-reset-rotation"
        />
      )}
      <Divider />
      <SectionValueField
        label={intl.formatMessage(messages.sectionOffset)}
        unit="mm"
        value={offset}
        onCommit={onOffsetChange}
        testId="model-viewer__section-offset"
      />
      {tilted && (
        <>
          <SectionValueField
            label={rotate(first)}
            unit="°"
            value={section.tilt[0]}
            onCommit={(degrees) => setTilt(0, degrees)}
            testId="model-viewer__section-tilt-0"
          />
          <SectionValueField
            label={rotate(second)}
            unit="°"
            value={section.tilt[1]}
            onCommit={(degrees) => setTilt(1, degrees)}
            testId="model-viewer__section-tilt-1"
          />
        </>
      )}
      <Divider />
      <ChromeButton
        label={intl.formatMessage(messages.sectionExit)}
        icon={<PiX />}
        onClick={onExit}
        testId="model-viewer__section-exit"
      />
    </Bar>
  );
});

export default SectionBar;
